import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import ProductGrid from "../containers/orders/ProductGrid";
import CartPanel from "../containers/orders/CartPanel";
// The same dialog the menu board uses, so an item added mid-service is the
// same item, with the same fields and the same picture, as one added in Menu.
import MenuItemDialog from "../containers/menu/MenuItemDialog";
import { createMenuItem } from "../containers/menu/menuApi";

import {
  setCategories,
  setCategoryDataFetched,
  selectCategory,
} from "../containers/orders/categorySlice";

import {
  addProduct,
  setProducts,
  setProductDataFetched,
} from "../containers/orders/productSlice";

import { clearCart } from "../containers/orders/orderSlice";
import useReadData from "../hooks/useReadData";
import { constants } from "../Helpers/constantsFile";

const OrderPage = () => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();

  const { categories, selected } = useSelector(state => state.categories);
  const { products } = useSelector(state => state.products);
  const { business } = useSelector(state => state.login.activeUser);
  const activeUser = useSelector(state => state.login.activeUser);
  const token = useSelector(state => state.login.token);

  // Waiters take orders. Changing the menu is not their job, and the button
  // sits right beside the search box they use constantly.
  const canEditMenu = activeUser?.role !== "waiter";

  const urlProduct = `${constants.baseUrl}/menu/get-business-menu-items/${business?._id}`;
  const urlCategory = `${constants.baseUrl}/menu-categories/get-business-categories/${business?._id}`;

  useReadData(
    urlProduct,
    setProducts,
    setProductDataFetched,
    state => state.products.isProductsDataFetched,
    "menuItems"
  );

  useReadData(
    urlCategory,
    setCategories,
    setCategoryDataFetched,
    state => state.categories.isCategoryDataFetched,
    "categories"
  );

  /**
   * Leaving this tab empties the cart.
   *
   * A half-built order left sitting behind the Orders or Customers tab is a
   * trap: the next person walks up, adds two things, and rings up somebody
   * else's items along with their own. That includes an order opened for
   * editing — coming back to a stale editingOrderId would silently overwrite
   * an order nobody meant to touch.
   *
   * Loading an order to edit still works: that dispatch happens on the orders
   * list, before this page mounts, while this only fires as it unmounts.
   */
  useEffect(() => {
    return () => {
      dispatch(clearCart());
    };
  }, [dispatch]);

  const addMenuItem = async ({ name, price, category, imageUrl }) => {
    const created = await createMenuItem({
      payload: { name, price, category, imageUrl, business: business?._id },
      token,
    });
    dispatch(addProduct(created));
  };

  // ✅ FILTER PRODUCTS (ALL = NO FILTER)
  const filteredProducts =
    selected === "All"
      ? products
      : products.filter(p => p.category === selected);

  return (
    <div style={{ display: "flex", gap: 10, width: "100%", }}>
      <div
        style={{
          flex: 3,
          background: "#FAFAFA",
          border: "1px solid #D9D9D9",
          borderRadius: "10px",
          padding: "20px",
          margin: "10px 20px",
          height: "85vh",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <input
            placeholder="Search products"
            style={{
              border: "1px solid #D4D4D4",
              padding: "12px",
              borderRadius: "10px",
              width: "50%",
            }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          {canEditMenu && (
          <button
            onClick={() => setOpen(true)}
            style={{
              background: "black",
              color: "white",
              width: "120px",
              borderRadius: "10px",
              fontWeight: "bold",
              cursor:"pointer"
            }}
          >
            New Product
          </button>
          )}
        </div>

        {/* ✅ CATEGORY BAR (NO BLANK CATEGORIES) */}
        <div
          style={{
            display: "flex",
            gap: 10,
            margin: "15px 0",
            flexWrap: "wrap",
          }}
        >
          {/* ALL */}
          <button
            onClick={() => dispatch(selectCategory("All"))}
            style={{
              padding: "6px 14px",
              borderRadius: 20,
              border: "1px solid #ddd",
              background: selected === "All" ? "#000" : "#fff",
              color: selected === "All" ? "#fff" : "#000",
              cursor: "pointer",
            }}
          >
            All
          </button>

          {/* REAL CATEGORIES ONLY */}
          {categories
            .filter(cat => cat && cat._id && cat.name)
            .map(cat => (
              <button
                key={cat._id}
                onClick={() => dispatch(selectCategory(cat._id))}
                style={{
                  padding: "6px 14px",
                  borderRadius: 20,
                  border: "1px solid #ddd",
                  background: selected === cat._id ? "#000" : "#fff",
                  color: selected === cat._id ? "#fff" : "#000",
                  cursor: "pointer",
                }}
              >
                {cat.name}
              </button>
            ))}
        </div>

        {/* ✅ PRODUCTS */}
        <ProductGrid
          search={search}
          products={filteredProducts}
        />
      </div>

      <div
        style={{
          flex: 1.25,
          background: "#FAFAFA",
          border: "1px solid #D9D9D9",
          borderRadius: "10px",
          padding: "20px",
          margin: "10px 20px",
        }}
      >
        <CartPanel />
      </div>

      {open && (
        <MenuItemDialog
          categories={categories}
          onClose={() => setOpen(false)}
          onSubmit={addMenuItem}
        />
      )}
    </div>
  );
};

export default OrderPage;
